import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Rocket, Mail, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, auth, errMsg } from "../../lib/api";
import { scanContext } from "./tracking";

const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-amber-400/60 focus:outline-none placeholder:text-slate-500";
const Field = ({ label, children }) => <label className="block"><span className="text-[11px] uppercase tracking-wider text-slate-400 font-medium block mb-1">{label}</span>{children}</label>;

const Shell = ({ title, subtitle, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid={testid}>
    <div className="w-full sm:max-w-md bg-elevated border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 max-h-[92vh] overflow-y-auto">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div><h3 className="font-display text-lg font-bold">{title}</h3>{subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}</div>
        <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid={`${testid}-close`}><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

export const GuestModal = ({ data, onClose }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", email: "", candidate_id: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: res } = await api.post("/public/scan/guest", { ...form, email: form.email || null, candidate_id: form.candidate_id || null, race_id: data.race.race_id, ...scanContext() });
      auth.login(res.token, res.user);
      toast.success(`Welcome, ${res.user.name.split(" ")[0]} — your ${data.race.short} demo workspace is ready`);
      navigate("/dashboard", { replace: true });
    } catch (err) { toast.error(errMsg(err, "Could not start the demo")); setBusy(false); }
  };
  return (
    <Shell title="Explore the live workspace" subtitle={`A read-only ${data.guest_session_minutes}-minute sandbox pre-loaded with ${data.race.label}. No password, nothing to install.`} onClose={onClose} testid="guest-modal">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Your name *"><input required value={form.name} onChange={set("name")} placeholder="Jordan Alvarez" className={inputCls} data-testid="guest-name-input" /></Field>
        <Field label="Mobile number *"><input required type="tel" value={form.phone} onChange={set("phone")} placeholder="(512) 555-0100" className={inputCls} data-testid="guest-phone-input" /></Field>
        <Field label="Email (optional — we'll send your workspace link)"><input type="email" value={form.email} onChange={set("email")} placeholder="you@campaign.org" className={inputCls} data-testid="guest-email-input" /></Field>
        <Field label="I'm with…">
          <select value={form.candidate_id} onChange={set("candidate_id")} className={inputCls} data-testid="guest-candidate-select">
            <option value="">Just exploring the race</option>
            {data.candidates.map((c) => <option key={c.id} value={c.id}>{`${c.display_name} (${c.party})`}</option>)}
          </select>
        </Field>
        <button type="submit" disabled={busy} data-testid="guest-start-btn"
          className="w-full rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 text-on-accent font-bold py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Open my demo workspace
        </button>
        <p className="text-[11px] text-slate-500 flex items-start gap-1.5"><Clock className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Demo mode is read-only and expires automatically. We&apos;ll only use your details to follow up about TotalReach.</p>
      </form>
    </Shell>
  );
};

export const LeadModal = ({ data, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: res } = await api.post("/public/scan/lead", { ...form, race_id: data.race.race_id, ...scanContext() });
      setDone(res.message);
    } catch (err) { toast.error(errMsg(err, "Could not send the report")); }
    finally { setBusy(false); }
  };
  return (
    <Shell title="Send me this race report" subtitle="Candidates, FEC money, tracking snapshot and CTV reach — in your inbox, plus a link back to this demo." onClose={onClose} testid="lead-modal">
      {done ? (
        <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-4 text-sm text-emerald-100 flex gap-2" data-testid="lead-success"><Check className="w-5 h-5 shrink-0" /> {done}</div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <Field label="Your name *"><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="lead-name-input" /></Field>
          <Field label="Email *"><input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="lead-email-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone"><input type="tel" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="lead-phone-input" /></Field>
            <Field label="Campaign / org"><input value={form.organization} onChange={set("organization")} className={inputCls} data-testid="lead-org-input" /></Field>
          </div>
          <button type="submit" disabled={busy} data-testid="lead-submit-btn"
            className="w-full rounded-xl border border-amber-400/50 text-amber-200 font-semibold py-3 inline-flex items-center justify-center gap-2 hover:bg-amber-400/10 disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Email me the report
          </button>
        </form>
      )}
    </Shell>
  );
};
